import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import DreamChatInterface from './components/DreamChatInterface';
import DreamDiary from './components/DreamDiary';
import DreamStats from './components/DreamStats';
import SEOSchema from '../../components/feature/SEOSchema';
import { useState } from 'react';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const dreamSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      '@id': `${siteUrl}/dream`,
      name: '꿈해몽 AI — 전통 해몽 + 동양 오행 + 심리학 분석',
      description: '수천 년 한국 전통 해몽과 사주 오행, 서양 심리학을 결합한 AI 꿈 풀이 상담 서비스. 돼지꿈, 뱀꿈, 용꿈 등 모든 꿈을 종합 해석합니다.',
      url: `${siteUrl}/dream`,
      provider: { '@type': 'Organization', name: 'DESTINY GPT', url: siteUrl },
      serviceType: '꿈해몽 AI 분석',
      areaServed: { '@type': 'Country', name: '대한민국' },
      inLanguage: 'ko-KR',
    },
    {
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '돼지꿈은 무슨 뜻인가요?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '돼지꿈은 전통적으로 재물과 풍요의 상징입니다. 특히 살찐 돼지나 여러 마리의 돼지가 나오는 꿈은 재물운이 크게 들어올 것을 예시합니다.',
          },
        },
        {
          '@type': 'Question',
          name: '용꿈의 의미는 무엇인가요?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '용꿈은 한국 전통 해몽에서 최고의 길몽 중 하나입니다. 출세운과 승진, 큰 성취를 상징하며 특히 하늘로 올라가는 용꿈은 대길몽으로 해석됩니다.',
          },
        },
      ],
    },
  ],
};

const dreamSymbols = [
  { icon: 'ri-moon-fill', label: '꿈의 상징', desc: '동양 해몽 전통' },
  { icon: 'ri-sparkling-2-fill', label: '오행 연결', desc: '사주와 꿈의 관계' },
  { icon: 'ri-psychology-fill', label: '심리 분석', desc: '융 & 프로이트 이론' },
  { icon: 'ri-compass-discover-fill', label: '길흉 판단', desc: '길몽 / 흉몽 분류' },
];

const dreamTypes = [
  { emoji: '🐷', name: '돼지 꿈', desc: '재물과 풍요의 상징', luck: '재물운 대길', category: '길몽' },
  { emoji: '🐍', name: '뱀 꿈', desc: '지혜·변화·재생', luck: '재물·직업운', category: '길몽' },
  { emoji: '🐉', name: '용 꿈', desc: '한국 해몽 최고 길몽', luck: '출세운 최길', category: '길몽' },
  { emoji: '💧', name: '물 꿈', desc: '감정·잠재의식 반영', luck: '감정·사업운', category: '길몽' },
  { emoji: '🔥', name: '불 꿈', desc: '변혁과 강한 에너지', luck: '사업운 대길', category: '길몽' },
  { emoji: '🪄', name: '하늘 나는 꿈', desc: '자유와 성공의 욕망', luck: '도전운 최고', category: '길몽' },
  { emoji: '💀', name: '누군가 죽는 꿈', desc: '가족·지인이 죽는 장면', luck: '오히려 길몽', category: '관계' },
  { emoji: '💔', name: '전남친 나오는 꿈', desc: '헤어진 연인이 꿈에 등장', luck: '감정 정리 신호', category: '관계' },
  { emoji: '😱', name: '바람피는 꿈', desc: '남편/아내의 불륜 장면', luck: '신뢰 점검 필요', category: '관계' },
  { emoji: '💍', name: '결혼하는 꿈', desc: '갑작스런 결혼식 장면', luck: '변화·결합의 징조', category: '관계' },
  { emoji: '🦷', name: '이가 빠지는 꿈', desc: '치아가 흔들리거나 빠짐', luck: '가족 건강 경고', category: '흉몽' },
  { emoji: '🏃', name: '쫓기는 꿈', desc: '누군가에게 쫓겨 도망침', luck: '스트레스·압박감', category: '흉몽' },
  { emoji: '📉', name: '시험 망하는 꿈', desc: '시험지 공백·지각 장면', luck: '준비 재점검 신호', category: '흉몽' },
  { emoji: '🌊', name: '홍수·침수 꿈', desc: '물에 잠기거나 떠내려감', luck: '감정 폭발 경고', category: '흉몽' },
  { emoji: '🛸', name: '추락하는 꿈', desc: '높은 곳에서 떨어지는 꿈', luck: '불안·위기 신호', category: '흉몽' },
  { emoji: '👶', name: '아기 꿈', desc: '갓난아기를 안거나 낳음', luck: '새 시작·태몽', category: '길몽' },
  { emoji: '💵', name: '돈 줍는 꿈', desc: '길에서 지폐를 줍는 꿈', luck: '재물운 상승', category: '길몽' },
  { emoji: '🐻', name: '맹수 쫓기는 꿈', desc: '곰·사자에게 쫓기는 꿈', luck: '강한 경쟁자 등장', category: '흉몽' },
];

const DREAM_CATEGORIES = ['전체', '길몽', '관계', '흉몽'] as const;
type DreamCategory = typeof DREAM_CATEGORIES[number];

export default function DreamPage() {
  const [activeCategory, setActiveCategory] = useState<DreamCategory>('전체');

  const filteredDreamTypes = activeCategory === '전체'
    ? dreamTypes
    : dreamTypes.filter((d) => d.category === activeCategory);

  return (
    <div className="min-h-screen bg-cosmic-gradient">
      <SEOSchema schema={dreamSchema} id="dream-schema" />
      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* Hero section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 25 }).map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-primary-gold/20 animate-pulse"
                style={{
                  width: `${Math.random() * 4 + 2}px`,
                  height: `${Math.random() * 4 + 2}px`,
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 3}s`,
                  animationDuration: `${Math.random() * 3 + 2}s`,
                }}
              />
            ))}
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 pb-8 sm:pt-14 sm:pb-10 text-center relative z-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-gold/10 border border-primary-gold/30 text-primary-gold text-xs sm:text-sm font-semibold mb-6 animate-fade-in">
              <i className="ri-moon-clear-fill text-base animate-pulse"></i>
              <span>AI 꿈해몽 · 전통 해몽 + 동양 오행 + 심리학</span>
            </div>

            <h1
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-4 sm:mb-5 leading-tight"
              style={{ fontFamily: '"Cinzel", "Noto Serif KR", serif' }}
            >
              꿈해몽
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-gold via-amber-300 to-primary-gold">
                종합 AI 상담
              </span>
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-purple-200/80 max-w-2xl mx-auto leading-relaxed mb-8">
              수천 년 한국 전통 해몽과 사주 오행, 서양 심리학을 결합한
              <br className="hidden sm:block" />
              가장 깊이 있는 꿈 풀이 AI 상담사
            </p>

            {/* Feature badges */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-10">
              {dreamSymbols.map((s) => (
                <div
                  key={s.label}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-primary-gold/20 hover:border-primary-gold/50 hover:bg-primary-gold/10 transition-all duration-300"
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    <i className={`${s.icon} text-primary-gold text-base`}></i>
                  </div>
                  <div className="text-left">
                    <p className="text-white text-xs font-bold">{s.label}</p>
                    <p className="text-purple-300/60 text-[10px]">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Chat interface */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-12">
          <DreamChatInterface />
        </div>

        {/* Dream types reference section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">자주 나오는 꿈 유형</h2>
            <p className="text-purple-300/60 text-sm mb-5">궁금한 꿈 유형을 채팅에서 바로 물어보세요</p>
            {/* Category tabs */}
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10">
              {DREAM_CATEGORIES.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-primary-gold text-primary-dark'
                      : 'text-purple-300/70 hover:text-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4">
            {filteredDreamTypes.map((dt) => (
              <div
                key={dt.name}
                className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all duration-300 cursor-default group ${
                  dt.category === '흉몽'
                    ? 'bg-red-900/8 border-red-500/15 hover:border-red-500/40 hover:bg-red-900/15'
                    : dt.category === '관계'
                    ? 'bg-pink-900/8 border-pink-500/15 hover:border-pink-400/40 hover:bg-pink-900/15'
                    : 'bg-white/5 border-primary-gold/15 hover:border-primary-gold/40 hover:bg-primary-gold/8'
                }`}
              >
                <span className="text-3xl group-hover:scale-110 transition-transform duration-300">{dt.emoji}</span>
                <p className="text-white text-xs font-bold text-center">{dt.name}</p>
                <p className="text-purple-300/60 text-[10px] text-center leading-tight">{dt.desc}</p>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap border ${
                  dt.category === '흉몽'
                    ? 'bg-red-500/10 border-red-500/25 text-red-400'
                    : dt.category === '관계'
                    ? 'bg-pink-500/10 border-pink-400/25 text-pink-300'
                    : 'bg-primary-gold/10 border-primary-gold/25 text-primary-gold'
                }`}>
                  {dt.luck}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Info section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 mb-4">
                <i className="ri-ancient-pavilion-fill text-amber-400 text-lg"></i>
              </div>
              <h3 className="text-white font-bold mb-2 text-base">한국 전통 해몽</h3>
              <p className="text-purple-300/70 text-sm leading-relaxed">수천 년 내려온 동양의 꿈 해석 전통. 오행(五行)과 음양(陰陽)의 원리를 바탕으로 길흉을 판단합니다.</p>
            </div>
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-indigo-500/10 border border-indigo-500/20 mb-4">
                <i className="ri-psychology-fill text-indigo-400 text-lg"></i>
              </div>
              <h3 className="text-white font-bold mb-2 text-base">심리학적 분석</h3>
              <p className="text-purple-300/70 text-sm leading-relaxed">칼 융(Carl Jung)의 원형 이론과 프로이트의 꿈 해석을 결합해 잠재의식의 메시지를 해독합니다.</p>
            </div>
            <div className="p-6 rounded-2xl bg-gradient-to-br from-primary-dark/80 to-mystic-dark/80 border border-primary-gold/20">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-emerald-500/10 border border-emerald-500/20 mb-4">
                <i className="ri-star-smile-fill text-emerald-400 text-lg"></i>
              </div>
              <h3 className="text-white font-bold mb-2 text-base">사주 오행 연결</h3>
              <p className="text-purple-300/70 text-sm leading-relaxed">꿈의 상징물을 사주 오행과 연결해 현재 대운의 흐름과 꿈의 관계를 종합적으로 풀이합니다.</p>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-gold/30 to-transparent"></div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-gold/10 border border-primary-gold/25">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-bar-chart-grouped-line text-primary-gold text-sm"></i>
              </div>
              <span className="text-primary-gold text-xs font-bold">꿈 통계 대시보드</span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-gold/30 to-transparent"></div>
          </div>
        </div>

        {/* Dream Stats */}
        <DreamStats />

        {/* Divider */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-10">
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-gold/30 to-transparent"></div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-gold/10 border border-primary-gold/25">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-book-2-fill text-primary-gold text-sm"></i>
              </div>
              <span className="text-primary-gold text-xs font-bold">꿈 일기장</span>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-primary-gold/30 to-transparent"></div>
          </div>
        </div>

        {/* Dream Diary */}
        <DreamDiary />
      </main>

      <Footer />
    </div>
  );
}
